import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ToastContext, Table, WeezHeader, Search, Card, ContentCard, Button, DropdownItem, Paginator } from '@weezevent/nacre';
import PartnerBatchBooking from '../../models/partnerBatchBooking';

import PartnerBatchBookingsRow from './partnerBatchBookingsRow';
import { NoItems } from '../../components/no-items';
import SortDropdown from '../../components/sortDropdown';
import DropdownPageSize from '../manager/components/dropdownPageSize';
import Auth from '../../utils/auth';
import Style from './styles/partnerBatchBookingsList.module.css';
import LoadingTable from '../../components/loadingTable';
import ConfirmModal from '../../components/confirmModal';
import { default as PartnerModel } from '../../models/partner';

export default function PartnerBatchBookingList({ organization, partnerId }) {
    const toast = React.useContext(ToastContext);

    const history = useHistory();
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = React.useState(0);
    const initialFilter = {
        offset: 0,
        limit: 25,
        search: null,
        ordering: '-created'
    };
    const [filter, setFilter] = React.useState(initialFilter);

    const [action, setAction] = React.useState(null);

    const [batchBookings, loading, sync] = PartnerBatchBooking.useApiModel(
        {
            organizationId: organization.id,
            partnerId,
            query: filter
        },
        [filter]
    );

    const total = React.useMemo(() => {
        return Number(batchBookings?.__meta.headers.get('total-count')) || 0;
    }, [batchBookings, loading]);

    const [partner, loadingPartner] = PartnerModel.useApiModel(
        {
            id: partnerId,
            organizationId: organization.id,
            onError: e => {
                console.error(e);
                toast.error(t('ticket.partners.retrieve.error'));
            }
        },
        [partnerId, organization]
    );

    const onChangeFilter = React.useCallback((key, value) => {
        setFilter(currentState => {
            let newFilter = { ...currentState };
            newFilter[key] = value;
            newFilter.offset = 0;

            return newFilter;
        });
        setCurrentPage(0);
    }, []);

    const onSearch = React.useCallback(
        (synth, value) => {
            onChangeFilter('search', value);
        },
        [onChangeFilter]
    );

    const onClick = React.useCallback(
        batchBookingId => {
            history.push(`/ticket/O${organization.id}/partners/${partnerId}/batches/${batchBookingId}`);
        },
        [history, organization, partnerId]
    );

    const onChangePageSize = React.useCallback(
        pageSize => {
            onChangeFilter('limit', pageSize);
        },
        [onChangeFilter]
    );

    const onChangePage = React.useCallback(page => {
        setFilter(currentState => {
            let newFilter = { ...currentState };
            newFilter.offset = page * newFilter.limit;

            return newFilter;
        });
        setCurrentPage(page);
    }, []);

    let handleReset = React.useCallback(() => {
        setFilter({ ...initialFilter });
        setCurrentPage(0);
    }, []);

    return (
        <>
            <WeezHeader
                title={t('ticket.partners.batch.listTitle')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/partners`) }}
                rightComponent={
                    <>
                        <Auth allow={['PyvarCapacity.ADMIN', 'PyvarCapacity.PARTNER']}>
                            <Button
                                disabled={partner?.isDeleted() || loadingPartner}
                                label={<span>{t('ticket.partners.batch.fillUp')}</span>}
                                onClick={() => {
                                    setAction({
                                        run: () => {
                                            return PartnerBatchBooking.fillUp({
                                                all_bookings: true,
                                                organizationId: organization.pk,
                                                partnerId
                                            })
                                                .then(() => {
                                                    toast.success(t('ticket.partners.batch.fillUpSuccess'));
                                                })
                                                .catch(e => {
                                                    console.error(e);
                                                    toast.error(t('ticket.partners.batch.fillUpError'));
                                                });
                                        }
                                    });
                                }}
                            />
                        </Auth>
                        <Button
                            primary
                            disabled={partner?.isDeleted() || loadingPartner}
                            label={t('ticket.partners.batch.addButton')}
                            onClick={() => history.push(`/ticket/O${organization.id}/partners/${partnerId}/batches/new`)}
                        />
                    </>
                }
            >
                {t('ticket.partners.batch.description')}
            </WeezHeader>

            <Card title={t('ticket.partners.batch.listBatchTitle')} subtitle={t('ticket.partners.batch.listBatchSubtitle', { count: total })}>
                <ContentCard>
                    <div className={Style['filters']}>
                        <Search onChange={onSearch} displayResetIcon={true} handleReset={handleReset} loading={false} placeholder={t('ticket.partners.batch.searchPlaceholder')} />
                        <DropdownPageSize
                            current={filter.limit}
                            onChange={onChangePageSize}
                            onChangeCurrentpage={setCurrentPage}
                            totalCount={total}
                            loading={loading}
                            pageSizes={[25, 50, 100]}
                            labelItem={'ticket.partners.batch.booking.pageSize'}
                        />
                    </div>
                    <Table useNew>
                        <Table.THead>
                            <Table.Tr>
                                <SortDropdown
                                    name="name"
                                    className={Style['width-regular']}
                                    ordering={filter.ordering}
                                    label={t('ticket.partners.batch.booking.name')}
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            item={t('ticket.common.ordering.A-Z')}
                                            checked={filter.ordering === 'name'}
                                            onClick={() => onChangeFilter('ordering', 'name')}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={t('ticket.common.ordering.Z-A')}
                                            checked={filter.ordering === '-name'}
                                            onClick={() => onChangeFilter('ordering', '-name')}
                                        />
                                    ]}
                                />
                                <th className={Style['width-small']}>{t('ticket.partners.batch.booking.batchRate')}</th>
                                <th className={Style['width-small']}>{t('ticket.partners.batch.booking.available')}</th>
                                <th className={Style['width-mini']}>{t('ticket.partners.batch.booking.commercialValue')}</th>
                                <SortDropdown
                                    name="created"
                                    className={Style['width-small']}
                                    ordering={filter.ordering}
                                    label={t('ticket.partners.batch.booking.creation')}
                                    items={[
                                        <DropdownItem.Icons
                                            key={1}
                                            item={t('ticket.common.ordering.descending')}
                                            checked={filter.ordering === '-created'}
                                            onClick={() => onChangeFilter('ordering', '-created')}
                                        />,
                                        <DropdownItem.Icons
                                            key={2}
                                            item={t('ticket.common.ordering.ascending')}
                                            checked={filter.ordering === 'created'}
                                            onClick={() => onChangeFilter('ordering', 'created')}
                                        />
                                    ]}
                                />
                                <th className={Style['width-regular']}>{t('ticket.partners.batch.booking.event')}</th>
                                <th className={Style['width-x-mini']}>{t('ticket.common.actions')}</th>
                            </Table.Tr>
                        </Table.THead>
                        <Table.TBody>
                            {batchBookings?.map(batchBooking => (
                                <PartnerBatchBookingsRow
                                    key={batchBooking.pk}
                                    batchBooking={batchBooking}
                                    onDelete={() => sync({ query: filter })}
                                    organizationId={organization.pk}
                                    partnerId={partnerId}
                                    onAction={setAction}
                                    onClick={onClick}
                                    partner={partner}
                                />
                            ))}
                        </Table.TBody>
                    </Table>
                    <LoadingTable loading={loading || loadingPartner} />
                    {!loading && batchBookings?.length < 1 && <NoItems title={t('ticket.partners.batch.no_batches')} />}
                    <div style={{ textAlign: 'center', margin: '10px 0' }}>
                        <Paginator
                            currentPage={currentPage}
                            onPageChange={numberPage => {
                                onChangePage(numberPage);
                            }}
                            totalCount={total}
                            pageSize={filter.limit}
                        />
                    </div>
                </ContentCard>
            </Card>
            {action && (
                <ConfirmModal
                    objectToSave={action}
                    onAccept={action => {
                        action.run().finally(() => setAction(null));
                    }}
                    onClose={() => setAction(null)}
                    labelTitle={action.title || t('ticket.partners.batch.actions.fillUp.modalTitle')}
                    labelSubtitle={action.subtitle}
                    labelBody={action.description || t('ticket.partners.batch.actions.fillUp.modalDescription')}
                />
            )}
        </>
    );
}

PartnerBatchBookingList.propTypes = {
    organization: PropTypes.object.isRequired,
    partnerId: PropTypes.string.isRequired
};
